/**
 * Header JavaScript Module
 * Handles navigation menu interactions, search functionality, and responsive behavior
 * 
 * @author T3 Bootstrap Team
 * @version 1.0.0
 */

// Search functionality
const initializeSearch = () => {
  const searchWrappers = document.querySelectorAll(".header-search");
  
  searchWrappers.forEach((searchWrapper) => {
    const searchBtn = searchWrapper.querySelector(".search-trigger");
    const icon = searchWrapper.querySelector("svg");
    
    if (!searchBtn || !icon) return;
    
    searchBtn.addEventListener("click", (e) => {
      e.preventDefault();
      searchWrapper.classList.toggle("search--open");
      
      const isOpen = searchWrapper.classList.contains("search--open");
      
      if (isOpen) {
        // Close icon
        icon.setAttribute("data-icon", "xmark");
        icon.setAttribute("viewBox", "0 0 448 512");
                 const pathElement = icon.querySelector("path");
         if (pathElement) {
           pathElement.setAttribute(
             "d",
             "M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c12.5 12.5 12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"
           );
         }
         const searchInput = searchWrapper.querySelector(".search-box .search-input");
         if (searchInput) {
           searchInput.focus();
         }
      } else {
        // Search icon
        icon.setAttribute("data-icon", "magnifying-glass");
        icon.setAttribute("viewBox", "0 0 512 512");
                 const pathElement = icon.querySelector("path");
         if (pathElement) {
           pathElement.setAttribute(
             "d",
             "M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z"
           );
         }
      }
    });
  });
};

/**
 * HeaderMenu Class
 * Manages navigation menu functionality including mobile menu, submenu positioning, and keyboard navigation
 */
export default class HeaderMenu {
  /**
   * Initialize HeaderMenu instance
   * @param {HTMLElement} element - The header element to initialize
   */
  constructor(element) {
    // Validate input
    if (!element || !(element instanceof HTMLElement)) {
      console.warn('HeaderMenu: Invalid element provided');
      return;
    }

    // Cache DOM elements
    this.element = element;
    this.body = document.body;
    this.menuBar = element.querySelector(".menu-trigger");
    this.mainMenu = element.querySelector("navigation");
    this.navItems = element.querySelectorAll("navigation nav ul li");
    this.navArrows = element.querySelectorAll(".navigation .has-sub>.nav-arrow");
    this.onePageLinks = element.querySelectorAll(
      "header.one-page-nav .navigation nav ul li a"
    );

    // Configuration
    this.config = {
      desktopBreakpoint: 992,
      submenuHideDelay: 100,
      resizeDebounceDelay: 250
    };

    // Initialize functionality
    this.initializeMenu();
  }

  /**
   * Initialize all menu functionality
   * @private
   */
  initializeMenu() {
    this.setupMenuTrigger();
    this.setupMobileMenu();
    this.setupOnePageNavigation();
    this.setupSubmenuPositioning();
  }

  /**
   * Setup mobile menu trigger
   * @private
   */
  setupMenuTrigger() {
    if (!this.menuBar) return;

    this.menuBar.addEventListener("click", () => {
      this.body.classList.toggle("menu--open");
      
      // Reset slide states
      this.navItems.forEach(item => {
        item.classList.remove("slide--up");
      });
    });
  }

  /**
   * Setup mobile submenu functionality
   * @private
   */
  setupMobileMenu() {
    this.navArrows.forEach(arrow => {
      arrow.addEventListener("click", () => {
        const parentNode = arrow.parentNode;
        this.removeActiveStateFromSiblings(parentNode);
        parentNode.classList.toggle("active");
        parentNode.classList.toggle("slide--up");
      });
    });
  }

  /**
   * Setup one-page navigation links
   * @private
   */
  setupOnePageNavigation() {
    this.onePageLinks.forEach(link => {
      link.addEventListener("click", () => {
        this.body.classList.toggle("menu--open");
      });
    });
  }

  /**
   * Remove active state from sibling elements
   * @param {HTMLElement} element - The element whose siblings should be deactivated
   * @private
   */
  removeActiveStateFromSiblings(element) {
    if (!element?.parentNode) return;

    const siblings = Array.from(element.parentNode.children).filter(
      sibling => sibling !== element && sibling.nodeType === 1
    );

    siblings.forEach(sibling => {
      sibling.classList.remove("active", "slide--up");
    });
  }

  /**
   * Initialize submenu positioning for desktop
   * @private
   */
  setupSubmenuPositioning() {
    // Only apply on desktop
    if (window.innerWidth >= this.config.desktopBreakpoint) {
      this.setupHoverPositioning();
      this.setupFocusPositioning();
    }

    // Handle responsive behavior
    this.setupResponsiveHandling();
  }

  /**
   * Setup responsive behavior for window resize
   * @private
   */
  setupResponsiveHandling() {
    let resizeTimeout;
    
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (window.innerWidth >= this.config.desktopBreakpoint) {
          this.setupHoverPositioning();
          this.setupFocusPositioning();
        }
      }, this.config.resizeDebounceDelay);
    };

    window.addEventListener('resize', handleResize);
  }

  /**
   * Setup hover-based submenu positioning
   * @private
   */
  setupHoverPositioning() {
    const menuItems = this.element.querySelectorAll('nav > ul > li.has-sub');
    
    menuItems.forEach(menuItem => {
      const submenu = menuItem.querySelector('ul');
      if (!submenu) return;

      this.setupHoverEvents(menuItem, submenu);
    });
  }

  /**
   * Setup hover events for a menu item
   * @param {HTMLElement} menuItem - The menu item element
   * @param {HTMLElement} submenu - The submenu element
   * @private
   */
  setupHoverEvents(menuItem, submenu) {
    // Show submenu on mouse enter
    menuItem.addEventListener('mouseenter', () => {
      this.closeAllSubmenus();
      this.showSubmenu(submenu);
    });

    // Hide submenu on mouse leave
    menuItem.addEventListener('mouseleave', () => {
      this.hideSubmenuWithDelay(menuItem, submenu);
    });

    // Handle submenu mouse leave
    submenu.addEventListener('mouseleave', () => {
      this.hideSubmenuWithDelay(menuItem, submenu);
    });
  }

  /**
   * Setup focus-based submenu positioning for keyboard navigation
   * @private
   */
  setupFocusPositioning() {
    const menuItems = this.element.querySelectorAll('nav > ul > li.has-sub');
    
    menuItems.forEach(menuItem => {
      const link = menuItem.querySelector('a');
      const submenu = menuItem.querySelector('ul');
      
      if (!link || !submenu) return;

      this.setupFocusEvents(menuItem, submenu);
    });
  }

  /**
   * Setup focus events for a menu item
   * @param {HTMLElement} menuItem - The menu item element
   * @param {HTMLElement} submenu - The submenu element
   * @private
   */
  setupFocusEvents(menuItem, submenu) {
    // Show submenu on focus
    const showSubmenuOnFocus = () => {
      this.closeAllSubmenus();
      this.showSubmenu(submenu);
    };

         const menuLink = menuItem.querySelector('a');
     if (menuLink) {
       menuLink.addEventListener('focus', showSubmenuOnFocus);
     }
    menuItem.addEventListener('focusin', showSubmenuOnFocus);

    // Hide submenu on blur
    menuItem.addEventListener('focusout', (e) => {
      const relatedTarget = e.relatedTarget;
      
      if (!menuItem.contains(relatedTarget)) {
        setTimeout(() => {
          if (!menuItem.contains(document.activeElement)) {
            this.hideSubmenu(submenu);
          }
        }, this.config.submenuHideDelay);
      }
    });
  }

  /**
   * Show a submenu
   * @param {HTMLElement} submenu - The submenu element to show
   * @private
   */
  showSubmenu(submenu) {
    if (!submenu) return;
    
    submenu.style.display = 'block';
    submenu.style.opacity = '1';
    submenu.style.pointerEvents = 'auto';
    submenu.style.zIndex = '999';
  }

  /**
   * Hide a submenu
   * @param {HTMLElement} submenu - The submenu element to hide
   * @private
   */
  hideSubmenu(submenu) {
    if (!submenu) return;
    
    submenu.style.display = 'none';
    submenu.style.opacity = '0';
    submenu.style.pointerEvents = 'none';
    submenu.style.zIndex = '-1';
  }

  /**
   * Hide submenu with delay to allow smooth navigation
   * @param {HTMLElement} menuItem - The menu item element
   * @param {HTMLElement} submenu - The submenu element
   * @private
   */
  hideSubmenuWithDelay(menuItem, submenu) {
    setTimeout(() => {
      const isMouseInMenu = menuItem.matches(':hover') || submenu.matches(':hover');
      if (!isMouseInMenu) {
        this.hideSubmenu(submenu);
      }
    }, this.config.submenuHideDelay);
  }

  /**
   * Close all submenus
   * @private
   */
  closeAllSubmenus() {
    const allSubmenus = this.element.querySelectorAll('nav > ul > li.has-sub > ul');
    allSubmenus.forEach(submenu => {
      this.hideSubmenu(submenu);
    });
  }
}

// Initialize search functionality
initializeSearch();

// Initialize HeaderMenu for all header elements
document.addEventListener('DOMContentLoaded', () => {
  const headerElements = document.querySelectorAll(".header-main");
  headerElements.forEach(headerElement => {
    new HeaderMenu(headerElement);
  });
});
